import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { VehicleSearchFilters } from '../types/manualsearch.types';
import type { VinVrmSearchInput } from '../types/vinvrmsearch.types';
import { getFilteredOptions, manualSearchVehicles, VINVRMSearch } from '../services';
import { vehicleSearchKeys } from '../services/queryKeys';
import { shouldRetryVinVrmSearch } from '../utils/vinVrmSearchRetry';

// Hook for getting filtered options (makes, models, etc.)
export const useFilteredOptionsQuery = (filters?: VehicleSearchFilters) => {
    return useQuery({
        queryKey: vehicleSearchKeys.filteredOptions(filters),
        queryFn: () => getFilteredOptions(filters || {}),
        staleTime: 5 * 60 * 1000, // 5 minutes - options don't change frequently
        gcTime: 10 * 60 * 1000, // 10 minutes
        enabled: true, // Always enabled as we need initial options
    });
};

// Hook for manual vehicle search
export const useManualVehicleSearchQuery = (
    filters: VehicleSearchFilters,
    enabled: boolean = false
) => {
    return useQuery({
        queryKey: vehicleSearchKeys.manualSearch(filters),
        queryFn: () => manualSearchVehicles(filters),
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
        enabled: enabled && !!(filters.make_id && filters.model_id), // Only search if make and model are selected
        retry: 1,
    });
};

// Hook for VIN/VRM search
export const useVinVrmSearchQuery = (query: VinVrmSearchInput, enabled: boolean = false) => {
    return useQuery({
        queryKey: vehicleSearchKeys.vinVrmSearch(query),
        queryFn: () => VINVRMSearch(query),
        staleTime: 60 * 60 * 1000, // 1 hour - VIN/VRM data is quite stable
        gcTime: 2 * 60 * 60 * 1000, // 2 hours
        enabled: enabled && !!(query.vin || query.vrm), // Only search if VIN or VRM is provided
        retry: shouldRetryVinVrmSearch,
    });
};

// Hook to prefetch related data
export const usePrefetchVehicleData = () => {
    const queryClient = useQueryClient();

    const filteredOptionsQuery = (filters?: VehicleSearchFilters) => ({
        queryKey: vehicleSearchKeys.filteredOptions(filters),
        queryFn: () => getFilteredOptions(filters || {}),
        staleTime: 5 * 60 * 1000,
    });

    const prefetchFilteredOptions = (filters?: VehicleSearchFilters) => {
        queryClient.prefetchQuery(filteredOptionsQuery(filters));
    };

    const fetchFilteredOptions = (filters: VehicleSearchFilters) =>
        queryClient.fetchQuery(filteredOptionsQuery(filters));

    return {
        prefetchFilteredOptions,
        fetchFilteredOptions,
    };
};
